import { Flex, useDisclosure } from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";

import { Button as ButtonJs } from "components/Button";
import { ButtonLink } from "components/ButtonLink";
import { TabHeader } from "components/TabHeader";
import ButtonViewGroup from "components/ButtonViewGroup/ButtonViewGroup";
import FilterButton from "components/FuncButton/FilterButton";
import BadgeComp from "components/RFQ/BadgeComp/BadgeComp";
import FilterSupplier from "components/RFQ/FilterSupplier/FilterSupplier";
import SearchBarJs from "components/RFQ/SearchBar/SearchBar";
import SortComp from "components/RFQ/SortComp/SortComp";
import { Icon } from "assets/Icon";
import { Pagination } from "newui/components/Pagination";
import GridViewSupplierLists from "pages/RFQ/SupplierList/GridViewSupplierLists";
import ListViewSupplierLists from "pages/RFQ/SupplierList/ListViewSupplierLists";
import { SupplierListStyled } from "pages/RFQ/SupplierList/styled";

import { useSupplierListsPaged } from "./hooks/useSupplierListsPaged";
import type {
	SupplierListDateFilters,
	SupplierListSort,
	SupplierListTab,
} from "./types/supplier-list.types";

// `Button` and `SearchBar` are legacy JS components whose PropTypes infer as
// all-required from TS. They have no other TS consumers, so we adapt them at
// this JS→TS boundary to a permissive prop type rather than threading dummy
// props through — behaviour is unchanged, only the compile-time shape.
type LooseComponent = (
	props: Record<string, unknown> & { children?: React.ReactNode }
) => React.ReactElement | null;
const Button = ButtonJs as unknown as LooseComponent;
const SearchBar = SearchBarJs as unknown as LooseComponent;

interface SupplierListsPageProps {
	// reach-router injects `path`; unused but part of the route element contract.
	path?: string;
}

interface SortOption {
	name: string;
	value: SupplierListSort;
	sort: string;
}

// Sort options for the paged screen (PRO-628): last-updated + alphabetical, both
// directions. Default (index 0) is last-updated, newest first ("activity desc").
const sortedData: SortOption[] = [
	{
		name: "Sort by Last Updated (Newest first)",
		value: "last_activity",
		sort: "Sort by: Last Updated (Newest)",
	},
	{
		name: "Sort by Last Updated (Oldest first)",
		value: "last_activity_asc",
		sort: "Sort by: Last Updated (Oldest)",
	},
	{
		name: "Sort Alphabetically (A to Z)",
		value: "asc",
		sort: "Sort by: A to Z",
	},
	{
		name: "Sort Alphabetically (Z to A)",
		value: "desc",
		sort: "Sort by: Z to A",
	},
];

const tabMenu: { id: SupplierListTab; name: string; showBadge: boolean }[] = [
	{ id: "myDepartment", name: "My Supplier Lists", showBadge: true },
	{ id: "companyApprovedList", name: "Panel Lists", showBadge: true },
];

const DEFAULT_PAGE_SIZE = 12;

export const SupplierListsPage = ({ path: _path }: SupplierListsPageProps) => {
	const [currentTab, setCurrentTab] = useState<SupplierListTab>("myDepartment");
	const [view, setView] = useState<"grid" | "list">("grid");
	const [sorted, setSorted] = useState<SortOption>(sortedData[0]);
	const [search, setSearch] = useState("");
	const [filters, setFilters] = useState<SupplierListDateFilters | null>(null);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [openPopUp, setOpenPopUp] = useState(false);
	const [badgeCounts, setBadgeCounts] = useState<Record<string, number>>({});

	const { isOpen, onOpen, onClose } = useDisclosure();

	// Any change to tab/search/sort/filters resets to the first page — a new
	// query key then triggers a single scoped fetch.
	useEffect(() => {
		setPage(1);
	}, [currentTab, search, sorted, filters]);

	const query = useSupplierListsPaged({
		tab: currentTab,
		page,
		limit: pageSize,
		search,
		sort: sorted.value,
		filters,
	});

	const items = query.data?.data ?? [];
	const meta = query.data?.meta;
	const counts = query.data?.counts;
	const totalItems = meta?.total ?? 0;
	const perPage = meta?.per_page ?? pageSize;

	// Show the loading state on the first load AND whenever we're fetching while
	// still displaying placeholder (previous) data — e.g. switching tabs or the
	// post-create refetch — so the user gets feedback instead of stale rows. A
	// same-tab background revalidation (not placeholder) won't flash skeletons.
	const isBusy = query.isLoading || (query.isFetching && query.isPlaceholderData);

	// Keep every tab badge populated. Preferred path: the API returns `counts`
	// for BOTH tabs, so all badges are correct on first load. Fallback (older API
	// builds that omit `counts`): track just the active tab's `meta.total` as
	// before, so the component still works if the FE ships ahead of the BE.
	//
	// Either way we ignore placeholder (keepPreviousData) results — while a new
	// tab loads, `meta`/`counts` still describe the PREVIOUS response and would
	// flash the wrong number against the new tab's badge.
	useEffect(() => {
		if (query.isPlaceholderData) {
			return;
		}

		if (counts) {
			setBadgeCounts((prev) => ({ ...prev, ...counts }));
			return;
		}

		if (typeof meta?.total === "number") {
			setBadgeCounts((prev) => ({ ...prev, [currentTab]: meta.total }));
		}
	}, [counts, meta?.total, currentTab, query.isPlaceholderData]);

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	const handleRowsPerPageChange = (newSize: number) => {
		setPageSize(newSize);
		setPage(1);
	};

	const pagination = useMemo(
		() => (
			<Pagination
				currentPage={page}
				rowsPerPage={perPage}
				rowsPerPageOptions={[12, 24, 36]}
				totalItems={totalItems}
				onPageChange={handlePageChange}
				onRowsPerPageChange={handleRowsPerPageChange}
			/>
		),
		[page, perPage, totalItems]
	);

	return (
		<SupplierListStyled>
			<TabHeader
				heading="Supplier Lists"
				description={`Create and manage your organisations supplier list.${currentTab === "companyApprovedList" ? " (Admin permissions only)" : ""}`}
				tooltip="A Supplier List includes your preferred Suppliers, such as Lead Architects, Tier 2 Contractors or Electrical Contractors."
			>
				<ButtonLink
					to="new-supplier-list"
					btnType="secondary"
					className="max-h-12"
					data-testid="create-supplier-list"
				>
					<Icon
						icon="plus"
						className="mr-1.5 [&>svg]:h-3 [&>svg]:w-3"
					/>
					Create Supplier List
				</ButtonLink>
			</TabHeader>
			<div id="layout-dashboard">
				<div>
					<Flex style={{ justifyContent: "space-between" }}>
						<Flex style={{ gap: "8px" }}>
							{tabMenu.map((tbMenu) => (
								<div
									key={tbMenu.id}
									className="group"
								>
									<Button
										btntype="tab"
										active={currentTab === tbMenu.id}
										onClick={() => setCurrentTab(tbMenu.id)}
										data-testid={`supplier-list-tab-${tbMenu.id}`}
									>
										<div
											style={{ fontFamily: "roboto", fontWeight: "500" }}
											className={`text-${currentTab === tbMenu.id ? "to-utility-brand-700" : "text-primary-500"}`}
										>
											{tbMenu.name}
										</div>
										{tbMenu.showBadge && (
											<BadgeComp
												title={tbMenu.id}
												active={currentTab}
												total={badgeCounts?.[tbMenu.id] ?? 0}
											/>
										)}
									</Button>
								</div>
							))}
						</Flex>
						<Flex style={{ gap: "16px" }}>
							<SearchBar setState={setSearch} />
							<FilterButton
								onClick={onOpen}
								filters={filters}
							/>
							<SortComp
								data={sortedData}
								sorted={sorted}
								onClickCB={(item: SortOption) => {
									setOpenPopUp(false);
									setSorted(item);
								}}
								openPopUpCB={openPopUp}
								setOpenPopUpCB={setOpenPopUp}
							/>
							<ButtonViewGroup
								setView={setView}
								view={view}
							/>
						</Flex>
					</Flex>
					<FilterSupplier
						isOpen={isOpen}
						onClose={onClose}
						setFilters={setFilters}
						filters={filters}
					/>
				</div>
				{pagination}
				{view === "grid" ? (
					<GridViewSupplierLists
						isLoading={isBusy}
						data={items}
						archived={false}
						currPage={page}
						currentTab={currentTab}
					/>
				) : (
					<ListViewSupplierLists
						isLoading={isBusy}
						data={items}
						archived={false}
						currPage={page}
						currentTab={currentTab}
					/>
				)}
				{pagination}
			</div>
		</SupplierListStyled>
	);
};
